'use client';

import FormText from '@/components/Form/Fields/FormText';
import { defaultCondition } from '@/components/Form/helper/conditionsHelper';
import { getFormFieldKeyRegistry } from '@/components/Form/helper/formFieldKeys';
import { isHidden, isRequired } from '@/components/Form/helper/formHelper';
import classes from '@/components/Form/styles/form.module.scss';
import {
  type ComponentType,
  type FormContentItem,
  type FormValues,
  getFormComponentByType,
} from '@/components/Form/types';
import type { FormInputStoryblok, FormStoryblok, FormTextStoryblok } from '@/types/component-types-sb';
import { isFormMarketingOptIn } from '@/types/typeguards';
import { storyblokEditable } from '@storyblok/react/rsc';
import { Field, useFormikContext } from 'formik';

interface FormFieldProps {
  fieldBlok: FormContentItem;
  index: number;
  formValues: FormValues;
  form: FormStoryblok;
}
const FormField = ({ fieldBlok, index, formValues, form }: FormFieldProps) => {
  const { errors, touched } = useFormikContext<FormValues>();

  // Do not render if it is hidden
  if (isHidden(fieldBlok, form, formValues)) {
    return null;
  }

  const Component = getFormComponentByType(fieldBlok.component as ComponentType);

  if (!Component) return null;

  if (fieldBlok.component === 'FormText') {
    return (
      <div className="fullWidth" {...storyblokEditable(fieldBlok)} id={fieldBlok.id ?? ''}>
        <FormText blok={fieldBlok as FormTextStoryblok} />
      </div>
    );
  }

  if (!!fieldBlok.type && fieldBlok.type === 'hidden') {
    const hiddenField = fieldBlok as FormInputStoryblok;
    const stateKey = getFormFieldKeyRegistry(form).getStateKey(fieldBlok);
    return (
      <>
        <input name={stateKey} value={(formValues?.[stateKey] as string) ?? ''} type="hidden" />
      </>
    );
  }

  let stateKey = getFormFieldKeyRegistry(form).getStateKey(fieldBlok);

  if (isFormMarketingOptIn(fieldBlok)) {
    stateKey = 'marketing_opt_in';
  }

  return (
    <div className={fieldBlok.layout ? `${fieldBlok.layout}Width` : 'fullWidth'} {...storyblokEditable(fieldBlok)}>
      <Field
        data-testid="form-builder-form-field"
        {...fieldBlok}
        key={fieldBlok._uid || `field-${index}`}
        className={classes.fullWidth}
        component={Component}
        name={stateKey}
        errorMessage={errors[stateKey]}
        required={isRequired(fieldBlok, form, formValues)}
        isInvalid={!!touched[stateKey] && !!errors[stateKey]}
        conditionMap={fieldBlok.conditions ? fieldBlok.conditions : defaultCondition}
        form={form}
      />
    </div>
  );
};

export default FormField;
